import React from "react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { accountingNumberFormatter } from "@common/helpers/number";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { buildGlDrillDownParams } from "../glDrillDown.lib";
import {
    EliminationExceptionsTableMeta,
    EliminationExceptionsViewRow,
} from "./eliminationExceptionsViewConfig";
import styles from "./EliminationExceptionsTable.module.scss";

type ExceptionCellContext = CellContext<
    ViewTableFeatures,
    EliminationExceptionsViewRow,
    unknown
>;

export const ExceptionEntityCell = React.memo(function ExceptionEntityCell({
    row,
}: ExceptionCellContext): React.ReactElement {
    return <ViewTableCell>{row.original.entityName}</ViewTableCell>;
});

export const ExceptionCounterpartyEntityCell = React.memo(
    function ExceptionCounterpartyEntityCell({
        row,
    }: ExceptionCellContext): React.ReactElement {
        return (
            <ViewTableCell>{row.original.counterpartyEntityName}</ViewTableCell>
        );
    },
);

export const ExceptionCounterpartyCell = React.memo(
    function ExceptionCounterpartyCell({
        row,
    }: ExceptionCellContext): React.ReactElement {
        return <ViewTableCell>{row.original.counterpartyName}</ViewTableCell>;
    },
);

export const ExceptionFactTypeCell = React.memo(function ExceptionFactTypeCell({
    row,
}: ExceptionCellContext): React.ReactElement {
    const { factTypeLabel, isInformational } = row.original;
    return (
        <ViewTableCell
            className={classNames({ [styles.mutedCell]: isInformational })}
        >
            {factTypeLabel}
        </ViewTableCell>
    );
});

export const ExceptionDateCell = React.memo(function ExceptionDateCell({
    row,
}: ExceptionCellContext): React.ReactElement {
    const { date, isInformational } = row.original;
    const className = classNames({ [styles.mutedCell]: isInformational });

    if (!date) {
        return <ViewTableCell className={className}>-</ViewTableCell>;
    }

    return (
        <ViewTableCell className={className}>
            {format(parseISO(date), "MM/dd/yyyy")}
        </ViewTableCell>
    );
});

export const ExceptionAccountCell = React.memo(function ExceptionAccountCell({
    row,
}: ExceptionCellContext): React.ReactElement {
    const { accountLabel, isInformational } = row.original;
    return (
        <ViewTableCell
            className={classNames({ [styles.mutedCell]: isInformational })}
        >
            {accountLabel}
        </ViewTableCell>
    );
});

export const ExceptionSourceCell = React.memo(function ExceptionSourceCell({
    row,
}: ExceptionCellContext): React.ReactElement {
    const { sourceLabel, isInformational } = row.original;
    return (
        <ViewTableCell
            className={classNames({ [styles.mutedCell]: isInformational })}
        >
            {sourceLabel}
        </ViewTableCell>
    );
});

export const ExceptionReferenceCell = React.memo(
    function ExceptionReferenceCell({
        row,
    }: ExceptionCellContext): React.ReactElement {
        const { journalEntries, isInformational } = row.original;
        const className = classNames({
            [styles.mutedCell]: isInformational,
        });

        if (journalEntries.length === 0) {
            return <ViewTableCell className={className}>-</ViewTableCell>;
        }

        return (
            <ViewTableCell className={className}>
                {journalEntries
                    .map((journalEntry) =>
                        getShortJournalEntryReferenceNumber(
                            journalEntry.referenceNumber,
                        ),
                    )
                    .join(", ")}
            </ViewTableCell>
        );
    },
);

export const ExceptionDescriptionCell = React.memo(
    function ExceptionDescriptionCell({
        row,
    }: ExceptionCellContext): React.ReactElement {
        const { description, isInformational } = row.original;
        const className = classNames({
            [styles.mutedCell]: isInformational,
        });

        if (description.length === 0) {
            return <ViewTableCell className={className}>-</ViewTableCell>;
        }

        return (
            <ViewTableCell className={className}>{description}</ViewTableCell>
        );
    },
);

export const ExceptionReasonCell = React.memo(function ExceptionReasonCell({
    row,
}: ExceptionCellContext): React.ReactElement {
    const { reasonLabel, isInformational } = row.original;
    return (
        <ViewTableCell
            className={classNames({ [styles.mutedCell]: isInformational })}
        >
            {reasonLabel}
        </ViewTableCell>
    );
});

// The residual amount is the row's deep-link: to the source transactions when
// present, otherwise the entity's general ledger filtered to the flip account.
export const ExceptionAmountCell = React.memo(function ExceptionAmountCell({
    row,
    table,
}: ExceptionCellContext): React.ReactElement {
    const {
        netAmount,
        isInformational,
        startDate,
        endDate,
        linkEntityId,
        linkAccountCode,
        transactionIds,
    } = row.original;
    const onDrillDown = useViewTableMeta<
        NonNullable<EliminationExceptionsTableMeta["callbacks"]>,
        unknown
    >(table).callbacks?.onDrillDown;

    const drillDownParams = buildGlDrillDownParams({
        startDate,
        endDate,
        entityId: linkEntityId,
        accountCodes:
            transactionIds.length === 0 && linkAccountCode !== undefined
                ? [linkAccountCode]
                : undefined,
        transactionIds: transactionIds.length > 0 ? transactionIds : undefined,
    });

    const formatted = accountingNumberFormatter(netAmount);

    return (
        <ViewTableCell
            className={classNames(styles.numericCell, {
                [styles.mutedCell]: isInformational,
            })}
        >
            {onDrillDown ? (
                <button
                    type="button"
                    className={classNames(
                        "data-table__drill-down",
                        styles.drillDownButton,
                    )}
                    onClick={() => onDrillDown(drillDownParams)}
                >
                    {formatted}
                </button>
            ) : (
                formatted
            )}
        </ViewTableCell>
    );
});

export const ExceptionActionsCell = React.memo(function ExceptionActionsCell({
    row,
    table,
}: ExceptionCellContext): React.ReactElement {
    const meta = table.options.meta as
        | EliminationExceptionsTableMeta
        | undefined;

    return (
        <ViewTableCell captureClicks>
            <Menu.More size="md" className={viewTableStyles.showOnHover}>
                <Menu.Item
                    onClick={() =>
                        meta?.callbacks?.onCreateJournalEntry(row.original)
                    }
                >
                    Create journal entry
                </Menu.Item>
                <Menu.Item
                    onClick={() =>
                        meta?.callbacks?.onCreateEliminatingJournalEntry(
                            row.original,
                        )
                    }
                >
                    Create eliminating journal entry
                </Menu.Item>
            </Menu.More>
        </ViewTableCell>
    );
});
